<template>
  <div>
    <v-container v-if="noDetail.status && !redirectionTowardSnomedPage" class="bg-grey100" data-testid="error-container">
      <h1 class="m-0 mb-2 text-center">Catalogue des concepts</h1>
      <v-row>
        <v-col>
          <AlertMessage :dismissable="false" type="error" data-testid="error-message">{{ noDetail.text }}</AlertMessage>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="auto" class="mx-auto">
          <a class=" btn btn--plain btn--primary" :href="'/' + $searchResultManager.conceptsCatalogSlug" data-testid="link-concepts-catalog">Catalogue des
            concepts</a>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="redirectionTowardSnomedPage" data-testid="snomed-licence-container">
      <SnomedBrowserLicenceComponent :isUserConnected="isUserConnected" :show="show" data-testid="snomed-licence-component"></SnomedBrowserLicenceComponent>
    </v-container>

    <v-container v-else fluid class="py-0" style="max-width: 90%;" data-testid="concept-detail-main">
      <v-row align="center">
        <v-col cols="auto">
          <TerminologyPageLink data-testid="terminology-page-link" />
        </v-col>
        <v-col cols="auto">
          <b data-testid="terminology-version">Version : {{ store.conceptDetail.versionTechniqueTermino }}</b>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <LanguageSelector v-if="store.terminologyLanguages.length > 0 && storeConfiguration.isCurrentLoaded"
            :languages="getLanguagesSorted" label="Afficher et rechercher en" :dense="true"
            @languageChanged="languageChanged" data-testid="language-selector" />
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>


      <div id="information-messages-container">
        <SimpleSnackbar v-for="(data, index) in store.conceptSnackbars" :key="index" :snackbarMessage="data.message"
          :snackbarColor="data.color" :snackbarIcon="data.icon" />
      </div>

      <v-row v-if="Object.keys(store.conceptDetail).length > 0 && isConfigurationLoaded && !redirectionTowardSnomedPage"
        :class="($vuetify.breakpoint.mdAndUp) ? 'd-flex flex-row no-gutters' : 'd-flex flex-column-reverse'"
        class="concept-details-container">
        <div class="flex-grow-0 flex-shrink-0"
          :style="($vuetify.breakpoint.mdAndUp) ? 'flex-basis: auto;' : 'flex-basis: auto; margin-top: 25px;'"
          data-testid="concepts-browser-panel">
          <ConceptsBrowser ref="conceptsBrowserComponent" data-testid="concepts-browser" />
        </div>
        <div class="flex-grow-1 flex-shrink-1"
          :style="($vuetify.breakpoint.mdAndUp) ? 'flex-basis: 0; padding-left: 15px; overflow-x: auto;' : 'flex-basis: 0; padding: 0; overflow-x: auto;'">

          <FiltresAxiomes ref="filtresAxiomes" data-testid="filtres-axiomes" />
          <div class="o-tabs js-tabs" data-testid="concept-tabs">
            <ul class="js-tablist p-0 concept-details-tabs" data-hx="h2" data-testid="tabs-list">
              <li class="js-tablist__item" data-testid="tab-item-detail">
                <a @click.stop.prevent="selectTab('detail')" class="js-tablist__link"
                  :aria-selected="selectedTab == 'detail'" data-testid="tab-detail">
                  Détail du concept
                </a>
              </li>

              <li class="js-tablist__item" data-testid="tab-item-mappings">
                <a @click.prevent="selectTab('mappings')" class="js-tablist__link"
                  :aria-selected="selectedTab == 'mappings'" data-testid="tab-mappings">
                  Correspondances dans les alignements ({{ store.conceptDetail.mappingsCount }})
                </a>
              </li>
            </ul>
            <div class="js-tabcontent--container" data-testid="tabs-content">
              <div v-if="selectedTab == 'detail'" class="js-tabcontent" data-testid="tabcontent-detail">
                <ConceptDetail v-if="storeConfiguration.isCurrentLoaded" :key="JSON.stringify(store.conceptDetail)"
                  :languages="store.terminologyLanguages" data-testid="concept-detail-panel" />
              </div>
              <div v-if="selectedTab == 'mappings'" class="js-tabcontent" data-testid="tabcontent-mappings">
                <p v-if="store.conceptDetail.mappingsCount == 0" class="text-center" data-testid="no-mappings-message">
                  Pas de correspondance dans une autre terminologie à afficher pour ce concept
                </p>
                <template v-else>
                  <AlertMessage v-if="showProtectedMappingsWarning" class="px-15 mb-8" data-testid="protected-mappings-warning">Attention: la visualisation de
                    certaines correspondances nécessite d'être connecté.</AlertMessage>

                  <ConceptDetail v-if="storeConfiguration.isCurrentLoaded"
                    :key="JSON.stringify(store.conceptDetail) + 'mappings'" :languages="store.terminologyLanguages"
                    displayMode="mappingsView" data-testid="concept-detail-mappings-panel" />

                  <MappingsTable :mappingListWithCounts="store.mappingListWithCounts"
                    :isUserConnected="isUserConnected" data-testid="mappings-table" />
                </template>
              </div>
            </div>
          </div>
        </div>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import ConceptsStore from "../concepts-store";
import Utils from "../utils";
import TreeUtils from "../tree-utils";
import StoreConfiguration from "../store-configuration";
import ConceptsBrowser from "../components/ConceptsBrowser";
import ConceptDetail from "../components/ConceptDetail";
import FiltresAxiomes from "../components/FiltresAxiomes";
import LanguageSelector from "../components/LanguageSelector";
import TerminologyPageLink from "../components/TerminologyPageLink";
import AlertMessage from "../components/AlertMessage";
import SnomedBrowserLicenceComponent from "../components/snomed/SnomedBrowserLicenceComponent";
import Snomed from "../snomed";
import MappingsTable from "../components/mappings/MappingsTable";
import TokenService from "../../../common/token.service";
import SimpleSnackbar from "../../../common/SimpleSnackbar";

export default {
  name: 'App',

  components: {
    FiltresAxiomes,
    TerminologyPageLink,
    LanguageSelector,
    ConceptsBrowser,
    ConceptDetail,
    AlertMessage,
    SnomedBrowserLicenceComponent,
    MappingsTable,
    SimpleSnackbar
  },

  data: () => ({
    store: ConceptsStore.state,
    storeConfiguration: StoreConfiguration.state,
    noDetail: {
      status: false,
    },
    isConfigurationLoaded: false,
    redirectionTowardSnomedPage: false,
    isUserConnected: false,
    renderComponent: true,
    show: true,
    selectedTab: ""
  }),

  watch: {
    $route(to, from) {
      if (to.query.concept !== from.query.concept) {
        this.selectedTab = "detail";
        this.store.selectedTab = "detail";
      } else {
        this.selectedTab = ConceptsStore.getSelectedTab();
      }
    }
  },

  async beforeCreate() {
    await this.$router; // Wait for VueRouter to be set.
    this.noDetail.status = false;

    ConceptsStore.setTerminologyId(this.$route.params.terminologyId); // Set the terminology URI ID following the URL path

    let detailTermino = await ConceptsStore.getTerminologyDetail();
    let isTerminoSnomed = detailTermino?.terminology?.snomedCT || false;
    this.isUserConnected = await ConceptsStore.checkIfUserIsConnected();

    // Check si la termino est Snomed et que l'utilisateur n'est pas connecté et n'a pas le licence navigateur valide ( moins de 24h) dans le localStorage
    if (isTerminoSnomed && !this.isUserConnected && !Snomed.checkIfSnomedBrowerLicenceValid()) {
      this.redirectionTowardSnomedPage = true;
    }

    // Tester le cas où on doit afficher le lien vers la page d'enregistrement
    if (isTerminoSnomed && this.isUserConnected) {
      let token = await ConceptsStore.getAccessToken();
      let user = TokenService.parseJwt(token);

      // Vérifier que l'utilisateur n'a pas accepté la licence Snomed + date valide => ajout de la redirection vers la page d'enregistrement SNOMED
      if (!Snomed.checkIfUserHasSnomedLicenceOfTheSnomedTermino(detailTermino.terminology, user) && !Snomed.checkIfSnomedBrowerLicenceValid()) {
        // Redirection avec lien vers la page d'enregistrement Snomed car utilisateur connecté
        this.redirectionTowardSnomedPage = true;
      }
    }

    // Ne charger les infos que dans le cas ou on ne redirige pas vers la page NavigateurSnomed 
    if (this.redirectionTowardSnomedPage == false) {
      // Load configurations before loading details of one concept
      await StoreConfiguration.getDefaultConfiguration();
      await StoreConfiguration.getConceptConfiguration();
      // Set the concept URI following the URL query parameter
      if (this.$route.query.concept) {
        ConceptsStore.setConceptId(this.$route.query.concept);
      } else {
        await this.getDefaultConceptId().then(id => {
          if (id) {
            ConceptsStore.setConceptId(id)
            let query = {
              ...this.$route.query,
              concept: this.store.conceptId
            };

            this.$router.push({ query }).catch(() => { });
          } else if (this.noDetail.status == false) {
            // si une erreur est déjà présente il ne faut pas écraser le message.
            this.noDetail.status = true;
            this.noDetail.text = 'Cette terminologie n\'a pas de concept(s) racine(s). Veuillez contacter un administrateur.';
          }
        });
      }

      await ConceptsStore.getTerminologyLanguages();

      this.isConfigurationLoaded = true;
      // passer en paramètre que l'utilisateur a une licence navigateur Snomed valide
      await ConceptsStore.getConceptDetail(Snomed.checkIfSnomedBrowerLicenceValid())
        .catch((error) => { // Handle error
          this.noDetail.status = true;
          this.noDetail.text = 'Une erreur est survenue. Veuillez réessayer ultérieurement.';

          if (error.response && error.response.data) {
            this.noDetail.text = error.response.data;
          }
        });
    }
  },

  created() {
    this.$vuetify.goTo(0);
    this.selectedTab = ConceptsStore.getSelectedTab();

  },

  updated() {
    this.$vuetify.goTo(0);
    if (this.$refs.filtresAxiomes !== undefined) {
      this.$refs.filtresAxiomes.onLogicalAxiomesChange();
      this.$refs.filtresAxiomes.onAnonymousAxiomesChange();
    }
  },

  methods: {
    languageChanged(e) {
      this.$refs.conceptsBrowserComponent.languageChanged(e);
    },

    async getDefaultConceptId() {
      let ret = await TreeUtils.getRootConcepts(this.store.terminologyId, 10000, Snomed.checkIfSnomedBrowerLicenceValid(), this.$route.query.lang).then(roots => {
        roots = TreeUtils.sortTreeNodesByConf(roots, StoreConfiguration.getConceptTreeRootConfiguration(), this.$route.query.lang ? this.$route.query.lang : 'en');
        if (roots.length > 0) {
          return roots[0].id;
        }
        return undefined;
      }).catch((error) => { // Handle error
        this.noDetail.status = true;
        this.noDetail.text = 'Cette terminologie n\'a pas de concept(s) racine(s) récupérable. Veuillez contacter un administrateur.';
        if (error.response && error.response.data) {
          this.noDetail.text = error.response.data;
        }
        return null;
      });

      return ret;
    },
    /**
     * recharge la page après l'acceptation de la licence navigateur
     */
    async forceRerender() {
      location.reload();
    },

    selectTab(tab) {
      this.selectedTab = tab;
      ConceptsStore.setSelectedTab(tab);
    }
  },
  computed: {
    /**
     * Get languages sorted by taking into account the configuration.
     */
    getLanguagesSorted: function () {
      let languagesConf = Utils.convertObjectValuesToArray(this.storeConfiguration?.current?.concepts_details?.affichage?.ordre_langues);
      let sortedLanguages = Array.isArray(this.store.terminologyLanguages) ? [...this.store.terminologyLanguages].sort() : [];
      let languages = Utils.sortArrayByPriorityOrder(sortedLanguages, languagesConf);
      return languages;
    },

    /**
     * Check if we should show an alert message (user is not connected and at least one mapping is protected)
     */
    showProtectedMappingsWarning() {
      return !this.isUserConnected && this.store.mappingListWithCounts
        && this.store.mappingListWithCounts.numberOfProtectedMappings
        && this.store.mappingListWithCounts.numberOfProtectedMappings > 0;
    }
  }
};
</script>

<style lang="scss" scoped>
#information-messages-container {
  position: fixed;
  box-sizing: border-box;
  bottom: 0;
  right: 0;
  z-index: 1000;
  height: auto;
  width: 400px;
  background-color: transparent;
}

::v-deep #information-messages-container p {
  font-size: 14px;
}

.concept-details-container {
  min-height: 90vh;
}

.concept-details-tabs {
  margin-top: 1rem;
}
</style>
